import { useMemo, useState, useEffect } from 'react';
import { connect } from 'react-redux';

import { makeGetChecklist } from 'selectors/checklists';
import { fetchChecklist, updateChecklist, removeChecklist, createChecklistItems, updateChecklistItem } from 'actions/checklists';

import { reorderArrayById } from 'utils/helpers'
import { getNewOrder } from 'utils/dnd'

import CreateListTaskItem from 'components/CreateListTaskItem';
import ProgressBarWithText from 'components/ProgressBarWithText';
import FieldDefault from 'components/field-default';

import ChecklistItem from './ChecklistItem';
import ChecklistActions from './ChecklistActions';

import IconListChecks from 'icons/icon-list-checks';

import './Checklist.styles.css';

const Checklist = ({ id, checklist, canEdit }) => {
    const [editing, setEditing] = useState(checklist?.isNew && checklist?.name === 'Untitled');
    const [sortedItems, setSortedItems] = useState(null);

    const computedItems = useMemo(() => {
        return sortedItems || checklist?.items?.sort((a, b) => {
            return a.order - b.order;
        }) || [];
    }, [checklist, sortedItems]);

    const handleItemCreate = names => {
        if (Array.isArray(names)) {
            return createChecklistItems(id, names.map((name, index) => ({
                name,
                order: computedItems.length + index
            })));
        } else {
            return createChecklistItems(id, [{
                name: names,
                order: computedItems.length
            }]);
        }
    }

    const handleEditNameClick = () => {
        setEditing(true);
    }

    const handleRemove = () => {
        removeChecklist(id)
    }

    const handleEditComplete = name => {
        updateChecklist(id, { name });
        setEditing(false);
    }

    const handleMoveItem = (dragId, targetId, save) => {
        const sorted = reorderArrayById(computedItems, dragId, targetId);

        setSortedItems(sorted);

        if (save) {
            const nextIndex = sorted.findIndex(({ id }) => id === dragId);

            updateChecklistItem(id, dragId, {
                order: getNewOrder(sorted[nextIndex - 1], sorted[nextIndex + 1], sorted)
            });

            setSortedItems(null);
        }
    }

    if (!checklist) {
        fetchChecklist(id);

        return null;
    }

    return (
        <div className="checklist subtasks">
            <div className="checklist-head">
                <div className="checklist-title">
                    <div className="checklist-title-body">
                        {
                            editing ? (
                                <FieldDefault autoFocus={true} small={true} value={checklist.name} blurOnEnter={true} onBlur={handleEditComplete} />
                            ) : (
                                <h4>{ checklist.name }</h4>
                            )
                        }
                    </div>

                    {
                        canEdit ? (
                            <ChecklistActions
                                onEditNameClick={handleEditNameClick}
                                onRemove={handleRemove}
                            />
                        ) : null
                    }
                </div>

                <ProgressBarWithText value={checklist.items?.filter(item => item.completed).length || 0} total={checklist.items?.length || 0} />
            </div>

            <div className="content-panel-subtasks">
                {
                    computedItems.map((item, index) => (
                        <ChecklistItem
                            key={item.id}
                            checklistId={id}
                            index={index}
                            item={item}
                            canEdit={canEdit}
                            moveItem={handleMoveItem}
                        />
                    ))
                }

                {
                    canEdit ? (
                        <CreateListTaskItem key="createTaskListItem" autoCreate={false} onCreate={handleItemCreate} placeholder="Add checklist item..." />
                    ) : null
                }
            </div>
        </div>
    )
}

const makeMapStateToProps = () => {
    const getChecklist = makeGetChecklist();

    return ({ checklists }, ownProps) => {
        return {
            checklist: getChecklist(checklists.items, ownProps.id)
        }
    };
}

export default connect(makeMapStateToProps)(Checklist);
